import { useState, useMemo, useEffect } from 'react'
import Layout from '../../components/Layout.jsx'
import PageState from '../../components/PageState.jsx'
import AccountSelect from '../../components/AccountSelect.jsx'
import { DataTable } from '../../components/DataTable.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { getAccounts, getKeywords, getSalesMetrics } from '../../db/api.js'
import { useApiData } from '../../hooks/useApiData.js'
import { useCollapseSidebarOnOverlap } from '../../hooks/useSidebarOverlap.js'

// ─────────────────────────────────────────────────────────────────────────────
// Derivações de campos não persistidos na tabela google_ads_keywords
// (cluster/região). Orçamento dia segue como placeholder até o backend expor
// o budget por keyword (campanha) no payload.
// ─────────────────────────────────────────────────────────────────────────────

function deriveCluster(adGroupName) {
  if (!adGroupName || adGroupName === 'Sem grupo') return '—'
  return String(adGroupName).trim().toLowerCase()
}

const UF_REGEX = /\b(AC|AL|AP|AM|BA|CE|DF|ES|GO|MA|MT|MS|MG|PA|PB|PR|PE|PI|RJ|RN|RS|RO|RR|SC|SP|SE|TO)\b/i
function deriveRegion(campaignName) {
  if (!campaignName) return '—'
  const m = String(campaignName).match(UF_REGEX)
  return m ? m[1].toUpperCase() : '—'
}

const COLUMNS = [
  { key: 'keywordText', label: 'Palavra-chave', sortable: true, width: 240 },
  { key: 'cluster', label: 'Cluster', sortable: true },
  { key: 'campaignName', label: 'Campanha', sortable: true },
  { key: 'region', label: 'Região', sortable: true, align: 'center', width: 90 },
  { key: 'dailyBudget', label: 'Orçamento dia', format: 'currency', sortable: true, align: 'right' },
  { key: 'impressions', label: 'Impressões', format: 'number', sortable: true, align: 'right' },
  { key: 'clicks', label: 'Cliques', format: 'number', sortable: true, align: 'right' },
  { key: 'visits', label: 'Visitas', format: 'number', sortable: true, align: 'right' },
  { key: 'whatsappClicks', label: 'Clique WhatsApp', format: 'number', sortable: true, align: 'right' },
  { key: 'contacts', label: 'Contato', format: 'number', sortable: true, align: 'right' },
  { key: 'qualifiedLeads', label: 'Lead qualificado', format: 'number', sortable: true, align: 'right' },
  { key: 'sales', label: 'Vendas', format: 'number', sortable: true, align: 'right' },
  { key: 'cost', label: 'Investimento', format: 'currency', sortable: true, align: 'right' },
  { key: 'budgetUsage', label: '% Orçamento', format: 'percent', sortable: true, align: 'right' },
  { key: 'averageCpc', label: 'CPC Médio', format: 'currency', sortable: true, align: 'right' },
  { key: 'cpl', label: 'CPL Médio', format: 'currency', sortable: true, align: 'right' },
  { key: 'cplq', label: 'CPLQ', format: 'currency', sortable: true, align: 'right' },
  { key: 'ctr', label: 'CTR', format: 'percent', sortable: true, align: 'right' },
  { key: 'conversionRate', label: 'Taxa de conversão', format: 'percent', sortable: true, align: 'right' },
  { key: 'cac', label: 'CAC', format: 'currency', sortable: true, align: 'right' },
  { key: 'ticketMedio', label: 'Ticket Médio', format: 'currency', sortable: true, align: 'right' },
]

export default function Keywords() {
  // Período global (mesmo calendário/PeriodPicker da Dashboard) e conta
  // aplicam na hora, sem botão "Aplicar" — mesmo padrão funcional de
  // Dashboard/Google Ads.
  const { activeClient, period } = useApp()
  const activeClientId = activeClient?.id ?? null

  // Recolhe a sidebar só se o calendário/tabela não couber com ela aberta.
  useCollapseSidebarOnOverlap(1100)

  // Conta selecionada (vazio = todas) — propaga ao backend para filtrar na
  // origem (não confiar só no filtro do front, que pode falhar se tipagens
  // divergirem).
  const [accountIds, setAccountIds] = useState([])

  const { data: accountsData } = useApiData(getAccounts, [], { initialData: [] })

  const { data: keywordsData, loading: kwLoading, error: kwError, reload: reloadKw } = useApiData(
    () => getKeywords(period.start, period.end, accountIds),
    [period.start, period.end, accountIds.join(',')],
    { initialData: [] }
  )

  // CRM: cruzamos por accountId + adGroupId (UTM medium) para popular
  // visitas/whats/contato/leadQ/sales/revenue — não estão em google_ads_keywords.
  const { data: crmData, loading: crmLoading, error: crmError, reload: reloadCrm } = useApiData(
    () => getSalesMetrics(period.start, period.end),
    [period.start, period.end],
    { initialData: [] }
  )

  // Troca de cliente invalida seleção local de conta (id pode pertencer a
  // outro cliente) — mesmo padrão de LeadAnalysis.
  useEffect(() => {
    if (activeClientId === null) return
    setAccountIds((prev) => (prev.length ? [] : prev))
  }, [activeClientId])

  const isLoading = kwLoading || crmLoading
  const hasError = kwError || crmError

  const enriched = useMemo(() => {
    if (!keywordsData?.length) return []

    // Total de investimento (após filtro de conta já vir do backend) para
    // calcular o % de participação no orçamento — mesma semântica que a
    // referência visual (23,57% de uma keyword sobre o total da listagem).
    const totalCost = keywordsData.reduce((s, kw) => s + Number(kw.cost || 0), 0)

    return keywordsData.map(kw => {
      const accId = String(kw.accountId || '')
      const adGroupId = String(kw.adGroupId || '')
      const campaignId = String(kw.campaignId || '')

      const groupCrm = crmData.filter(s => {
        if (String(s.accountId) !== accId) return false
        const utmMedium = String(s.adGroupId || '')
        const utmContent = String(s.adId || '')
        return (
          utmMedium.includes(adGroupId) ||
          utmContent.includes(adGroupId) ||
          utmMedium.includes(campaignId)
        )
      })

      const visits = groupCrm.reduce((sum, s) => sum + Number(s.visits || 0), 0)
      const leads = groupCrm.reduce((sum, s) => sum + Number(s.leads || 0), 0)
      const whatsappClicks = groupCrm.reduce((sum, s) => sum + Number(s.whatsappClicks || 0), 0)
      const contacts = groupCrm.reduce((sum, s) => sum + Number(s.contacts || 0), 0)
      const qualifiedLeads = groupCrm.reduce((sum, s) => sum + Number(s.qualifiedLeads || 0), 0)
      const sales = groupCrm.reduce((sum, s) => sum + Number(s.sales || 0), 0)
      const revenue = groupCrm.reduce((sum, s) => sum + Number(s.revenue || 0), 0)

      const cost = Number(kw.cost || 0)
      const clicks = Number(kw.clicks || 0)
      const impressions = Number(kw.impressions || 0)

      return {
        ...kw,
        cluster: deriveCluster(kw.adGroupName),
        region: deriveRegion(kw.campaignName),
        dailyBudget: 0, // placeholder — backend ainda não expõe budget por keyword
        impressions,
        clicks,
        cost,
        visits,
        whatsappClicks,
        contacts,
        qualifiedLeads,
        sales,
        revenue,
        budgetUsage: totalCost > 0 ? (cost / totalCost) * 100 : 0,
        ctr: Number(kw.ctr || 0),
        averageCpc: Number(kw.averageCpc || 0),
        cpl: leads > 0 ? cost / leads : 0,
        cplq: qualifiedLeads > 0 ? cost / qualifiedLeads : 0,
        conversionRate: clicks > 0 ? (leads / clicks) * 100 : 0,
        cac: sales > 0 ? cost / sales : 0,
        ticketMedio: sales > 0 ? revenue / sales : 0,
      }
    })
  }, [keywordsData, crmData])

  // Filtro defensivo no front (backend já filtra — esta é uma rede de
  // segurança para o caso de mismatch de tipo).
  const filteredData = useMemo(() => {
    if (!accountIds.length) return enriched
    const targets = new Set(accountIds.map(String))
    return enriched.filter(item => targets.has(String(item.accountId)))
  }, [enriched, accountIds])

  return (
    <Layout
      title="Google Ads — Palavras-chave"
      breadcrumb={[{ label: 'Marketing' }, { label: 'Google Ads', to: '/google-ads' }, { label: 'Palavras-chave' }]}
      showPeriod
      topbarRight={<AccountSelect accounts={accountsData || []} selected={accountIds} onChange={setAccountIds} />}
    >
      <PageState
        loading={isLoading}
        error={hasError}
        loadingText="Carregando palavras-chave..."
        onRetry={() => { reloadKw(); reloadCrm() }}
      >
        <div className="table-section">
          <div className="table-section-header">
            <div>
              <div className="table-section-title">Dados por Palavra-chave</div>
              <div className="table-section-subtitle">
                Clique no cabeçalho da coluna para ordenar
              </div>
            </div>
          </div>
          <DataTable
            columns={COLUMNS}
            data={filteredData}
            hoverable
            striped
            scrollable
            maxHeight="calc(100vh - 240px)"
            emptyText="Nenhuma palavra-chave encontrada para os filtros selecionados"
          />
        </div>
      </PageState>
    </Layout>
  )
}
